import getConfiguration from '@/store/getConfiguration';
import {
  GetAvailabilityOutputDTO,
  LocationApi,
  ReadLocationResponse,
  TimeRange,
} from '@/api';
import { showError } from '@/mixins/toastService';
import { format } from 'date-fns';
import * as Sentry from '@sentry/vue';

async function getApi() {
  const configuration = await getConfiguration();
  return new LocationApi(configuration, '');
}

type InitialState = {
  isLoadingDockSchedule: boolean;
  dockSchedule?: GetAvailabilityOutputDTO;
  isLoadingLocations: boolean;
  locations: ReadLocationResponse[];
  selectedLocationId: string;
  selectedDate: Date | null;
  fetchAbortController: AbortController | null;
};

export const initialState = (): InitialState => ({
  isLoadingDockSchedule: false,
  dockSchedule: undefined,
  isLoadingLocations: false,
  locations: [],
  selectedLocationId: '',
  selectedDate: new Date(),
  fetchAbortController: null,
});
export type DockManagementState = ReturnType<typeof initialState>;
type ActionType = {
  commit: Function;
  state: DockManagementState;
};

export default {
  namespaced: true,
  state: initialState(),
  mutations: {
    resetState(state: DockManagementState) {
      Object.assign(state, initialState());
    },

    setDockSchedule(
      state: DockManagementState,
      dockSchedule: GetAvailabilityOutputDTO,
    ) {
      state.dockSchedule = dockSchedule;
    },
    setLoadingDockSchedule(state: DockManagementState, isLoading: boolean) {
      state.isLoadingDockSchedule = isLoading;
    },

    setLocations(
      state: DockManagementState,
      locations: ReadLocationResponse[],
    ) {
      state.locations = locations;
    },
    setLoadingLocations(state: DockManagementState, isLoading: boolean) {
      state.isLoadingLocations = isLoading;
    },

    setLocationId(state: DockManagementState, locationId: string) {
      state.selectedLocationId = locationId;
    },
    setDate(state: DockManagementState, date: Date) {
      state.selectedDate = date;
    },
  },
  actions: {
    async fetchDockSchedule({
      commit,
      state,
    }: {
      commit: Function;
      state: DockManagementState;
    }) {
      if (!state.selectedLocationId || !state.selectedDate) {
        return;
      }

      state.fetchAbortController?.abort();
      const controller = new AbortController();
      state.fetchAbortController = controller;

      try {
        commit('setLoadingDockSchedule', true);
        const api = await getApi();
        const { data } =
          await api.getAvailabilityDockschedulingLocationsLocationIdAvailabilityGet(
            state.selectedLocationId,
            format(state.selectedDate, 'yyyy-MM-dd'),
            { signal: controller.signal },
          );
        // Only commit if this request wasn't aborted
        if (!controller.signal.aborted) {
          commit('setDockSchedule', data);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          showError('dock-management.error-fetching-dock-schedule');
          Sentry.captureException(error);
        }
      } finally {
        if (!controller.signal.aborted) {
          commit('setLoadingDockSchedule', false);
        }
      }
    },
    async fetchLocations({ commit, state }: ActionType) {
      if (state.locations.length > 0) {
        return;
      }

      try {
        commit('setLoadingLocations', true);
        const api = await getApi();
        const { data } = await api.listLocationsDockschedulingLocationsGet();
        commit('setLocations', data);
      } catch (error) {
        Sentry.captureException(error);
        showError('dock-management.error-fetching-locations');
      } finally {
        commit('setLoadingLocations', false);
      }
    },
    async closeTimeslot(
      { state }: ActionType,
      {
        dockId,
        payload,
      }: {
        dockId: string;
        payload: TimeRange;
      },
    ) {
      if (!state.selectedLocationId || !state.selectedDate) return;
      try {
        const api = await getApi();
        await api.closeDockDockschedulingLocationsLocationIdDocksDockIdClosePut(
          state.selectedLocationId,
          dockId,
          {
            date: format(state.selectedDate, 'yyyy-MM-dd'),
            time_range: payload,
          },
        );
      } catch (error) {
        Sentry.captureException(error);
        showError('dock-management.error-locking-timeslot');
      }
    },
    async openTimeslot(
      { state }: ActionType,
      { dockId, payload }: { dockId: string; payload: TimeRange },
    ) {
      if (!state.selectedLocationId || !state.selectedDate) return;

      try {
        const api = await getApi();
        await api.openDockDockschedulingLocationsLocationIdDocksDockIdOpenPut(
          state.selectedLocationId,
          dockId,
          {
            date: format(state.selectedDate, 'yyyy-MM-dd'),
            time_range: payload,
          },
        );
      } catch (error) {
        Sentry.captureException(error);
        showError('dock-management.error-unlocking-dock');
      }
    },
  },
  getters: {
    allLocations(state: DockManagementState): ReadLocationResponse[] {
      return state.locations;
    },
  },
};
